import { useEffect } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { useRecoilState, useRecoilValue } from 'recoil';

import { ADOBE_API_TOKEN_KEY, apiConfig, DSP_CLIENT_TOKEN_KEY } from '@constants';
import { environmentsState, userAuthState } from '@store';

import { getDspAccessToken } from '../../helpers/DspAuth';
import { getDeviceKey } from '../../helpers/KeyUtils';
import { isFireTV, isGoogleTV, isTizen, isTVOS, isX1, platformName } from '../../helpers/Platform';
import { getStorageItem, setStorageItem } from '../../helpers/storage';

// device info
const deviceInfo = isFireTV
  ? `{
    "model": "AFTM",
    "vendor": "Amazon",
    "manufacturer": "Amazon",
    "osName": "Android",
    "osVendor": "Google",
    "osVersion": "montoya",
    "browserVendor": "Google",
    "browserName": "Chrome",
    "applicationId": "weatherchannel"
  }`
  : isGoogleTV
  ? `{
    "model": "AFTM",
    "vendor": "Google",
    "manufacturer": "Google",
    "osName": "Android",
    "osVendor": "Google",
    "osVersion": "montoya",
    "browserVendor": "Google",
    "browserName": "Chrome",
    "applicationId": "weatherchannel"
  }`
  : isTizen
  ? `{
    "model": "AFTM",
    "vendor": "Samsung",
    "manufacturer": "Samsung",
    "osName": "Tizen",
    "osVendor": "Samsung",
    "osVersion": "",
    "browserVendor": "",
    "browserName": "",
    "applicationId": "weatherchannel"
  }`
  : isX1
  ? `{
    "model": "AFTM",
    "vendor": "X1",
    "manufacturer": "X1",
    "osName": "X1",
    "osVendor": "X1",
    "osVersion": "",
    "browserVendor": "",
    "browserName": "",
    "applicationId": "weatherchannel"
  }`
  : isTVOS
  ? `{
    "model": "AFTM",
    "vendor": "Apple",
    "manufacturer": "Apple",
    "osName": "TVOS",
    "osVendor": "Apple",
    "osVersion": "",
    "browserVendor": "",
    "browserName": "",
    "applicationId": "weatherchannel"
  }`
  : `{
    "model": "AFTM",
    "vendor": "Amazon",
    "manufacturer": "Amazon",
    "osName": "Android",
    "osVendor": "Google",
    "osVersion": "",
    "browserVendor": "",
    "browserName": "",
    "applicationId": "weatherchannel"
  }`;

const deviceInfoString = CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(deviceInfo));

const registerHeaders = {
  'Content-Type': 'application/json',
  'X-Device-Info': deviceInfoString,
};
const accessTokenHeaders = {
  'Content-Type': 'application/x-www-form-urlencoded',
};

const registerDevice = () =>
  axios.post(
    apiConfig.ADOBE_TVE_REGISTER_URL,
    { software_statement: apiConfig.ADOBE_TVE_SOFTWARE_STATEMENT },
    {
      headers: registerHeaders,
    },
  );

const platform = platformName();

export const getToken = async (force = false) => {
  let token = force ? null : getStorageItem(ADOBE_API_TOKEN_KEY);

  if (!token) {
    await axios
      .post(
        apiConfig.ADOBE_TVE_REGISTER_URL,
        {
          software_statement: apiConfig.ADOBE_TVE_SOFTWARE_STATEMENT,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Info': deviceInfoString,
          },
        },
      )
      .then(response => response.data)
      .then(
        async authData =>
          await axios
            .post(
              `${apiConfig.ADOBE_TVE_ACCESS_TOKEN_URL}&client_id=${authData.client_id}&client_secret=${authData.client_secret}`,
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
              },
            )
            .then(async response => {
              setStorageItem(ADOBE_API_TOKEN_KEY, response.data.access_token);
              token = response.data.access_token;

              const clientToken = await fetchAdobeClientToken(token);

              setStorageItem(DSP_CLIENT_TOKEN_KEY, clientToken);
            }),
      );
  }
  return token;
};

const fetchToken = (clientID, clientSecret) =>
  axios.post(`${apiConfig.ADOBE_TVE_ACCESS_TOKEN_URL}&client_id=${clientID}&client_secret=${clientSecret}`, {
    headers: accessTokenHeaders,
  });

export const fetchAdobeClientToken = async userToken => {
  const { accessToken } = await getDspAccessToken(false);
  const guid = getDeviceKey();

  return axios
    .post(`${apiConfig.DSP_ADOBE_TOKEN_URL}&deviceId=${guid}&userToken=${userToken}`, null, {
      headers: {
        'Content-Type': 'application/json',
        'x-access-token': accessToken,
      },
    })
    .then(response => {
      const { success, result } = response?.data;

      return success ? result : '';
    })
    .catch(error => '');
};

export const getCode = async (token, guid) =>
  axios.post(`${apiConfig.ADOBE_TVE_REGCODE_URL}&deviceId=${guid}&deviceType=${platform}`, null, {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Device-Info': deviceInfoString,
    },
  });

export const getUserAuth = async () => {
  const guid = getDeviceKey();
  const reg = (await registerDevice()).data;
  const clientID = reg.client_id;
  const clientSecret = reg.client_secret;
  const token = (await fetchToken(clientID, clientSecret)).data.access_token;

  setStorageItem(ADOBE_API_TOKEN_KEY, token);

  return {
    token,
    guid,
    deviceInfoString,
  };
};
export const useGetAdobeTVEUserAuth = () => {
  const { environments } = useRecoilValue(environmentsState);
  const [state, setState] = useRecoilState(userAuthState);

  const { data, error, isFetched, isLoading } = useQuery(
    ['getUserAuth', environments.prod, environments.debug],
    getUserAuth,
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: true,
    },
  );

  useEffect(() => {
    setState({ data, error, isFetched, isLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isFetched, isLoading]);
  return state;
};
